import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { capsulas } from "@/data/capsulas";
import { blogPosts } from "@/data/blogPosts";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import AppleHero from "@/components/AppleHero";
import AppleSection from "@/components/AppleSection";
import AppleSplitComparison from "@/components/AppleSplitComparison";
import { DiscoveryPathway } from "@/components/DiscoveryPathway";
import { ServiceLevelSelector } from "@/components/ServiceLevelSelector";
import { CapsuleFAQ } from "@/components/CapsuleFAQ";
import { capsulaFAQs } from "@/data/capsulaFAQs";
import { shouldRecommendSupport } from "@/utils/serviceLevelRecommender";
import { extractBusinessTypes } from "@/utils/businessClassifier";
import type { BlogPost } from "@/data/blogPosts";
import { useSEO } from "@/hooks/useSEO";
import ToolListSection from "@/components/ToolListSection";

// Helper para obtener nombre de herramienta dinámicamente
const getToolName = (post: BlogPost): string => {
  if (post.tools && post.tools.length > 0) {
    return post.tools[0];
  }
  return post.title.split(':')[0].trim();
};

const CapsulaDetail = () => {
  const { capsulaId } = useParams();
  const navigate = useNavigate();
  const capsula = capsulas.find(c => c.id === capsulaId);

  useSEO({
    title: capsula ? `${capsula.title} | Cápsulas IA - Globaltis` : "Cápsula no encontrada | Globaltis IA",
    description: capsula?.description || "Cápsula IA no encontrada",
  });

  if (!capsula) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Cápsula no encontrada</h1>
          <Button onClick={() => navigate("/capsulas")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a Cápsulas
          </Button>
        </div>
      </div>
    );
  }

  const isVideoCreation = capsula.id === "creacion-videos";
  const isAutomatizaciones = capsula.id === "automatizacion-emails";

  // Get related articles
  const relatedArticles = capsula.relatedPostIds
    .map(id => blogPosts.find(post => post.id === id))
    .filter(Boolean) as typeof blogPosts;
  
  const recommendSupport = shouldRecommendSupport(relatedArticles);
  const businessData = extractBusinessTypes(capsula.relatedPostIds || []);

  if (!isVideoCreation && !isAutomatizaciones) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-24">
          <Button 
            variant="outline" 
            onClick={() => navigate("/capsulas")}
            className="mb-8"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a Cápsulas
          </Button>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light mb-8 tracking-tight">{capsula.title}</h1>
          <p className="text-xl sm:text-2xl md:text-3xl text-muted-foreground mb-16 font-light leading-relaxed">
            {capsula.description} Sin complicaciones técnicas.
          </p>
          
          {/* Service Level Selector */}
          <ServiceLevelSelector
            capsulaTheme={capsula.id}
            recommendSupport={recommendSupport}
          />

          {/* Herramientas enriquecidas */}
          {relatedArticles.length > 0 && (
            <ToolListSection
              articles={relatedArticles}
              ctaLabel="Ver más herramientas"
              ctaLink="/blog"
            />
          )}

          {/* Business Types Section - Dynamic */}
          <AppleSection background="black" padding="normal">
            <div className="text-center max-w-5xl mx-auto space-y-16">
              <p className="text-2xl md:text-3xl lg:text-4xl font-light text-white dark:text-black leading-relaxed">
                {businessData.types.join('. ')}.
              </p>

              {businessData.hasContentCreators && (
                <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white dark:text-black pt-12">
                  Si creas contenido,
                  <br />
                  esto es para ti.
                </p>
              )}
            </div>
          </AppleSection>

          {/* Discovery Pathway */}
          {relatedArticles.length > 0 && (
            <DiscoveryPathway
              articles={relatedArticles}
              capsulaTheme={capsula.id}
              problemStatement={relatedArticles[0]?.businessProblem}
            />
          )}

          {/* FAQ específico de la cápsula */}
          {capsulaFAQs[capsula.id] && (
            <CapsuleFAQ faqs={capsulaFAQs[capsula.id]} />
          )}
        </div>
        <Footer />
      </div>
    );
  }

  // Renderizado específico para AUTOMATIZACIONES
  if (isAutomatizaciones) {
    return (
      <div className="min-h-screen bg-white dark:bg-black">
        {/* Hero Section - Apple Style */}
        <AppleHero
          title={[
            "Trabaja menos.",
            "Factura igual.",
            "Sin empleados extra.",
            "Solo automatizaciones."
          ]}
          cta="Ver cómo funciona"
          ctaLink="#como-funciona"
        />

        {/* El Problema */}
        <AppleSection background="black" padding="large">
          <div className="text-center max-w-5xl mx-auto space-y-16">
            <p className="text-4xl md:text-5xl lg:text-6xl font-light text-white dark:text-black leading-tight animate-fade-in">
              Estás pagando 1.200€ al mes
              <br />
              por tareas que una IA hace en segundos.
            </p>
            <p className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-400 dark:text-gray-600 leading-tight">
              Emails, facturas, formularios, prospección...
              <br />
              Todo a mano. Todo repetitivo.
            </p>
          </div>
        </AppleSection>

        {/* La Solución - Tres Números Gigantes */}
        <AppleSection background="white" padding="large">
          <div className="text-center space-y-32 max-w-4xl mx-auto">
            <div className="space-y-6">
              <h2 className="text-7xl md:text-8xl lg:text-9xl font-bold text-black dark:text-white leading-none">
                15 horas
              </h2>
              <p className="text-xl md:text-2xl font-light text-gray-500">
                Recuperas cada semana
              </p>
            </div>

            <div className="space-y-6">
              <h2 className="text-7xl md:text-8xl lg:text-9xl font-bold text-black dark:text-white leading-none">
                10€
              </h2>
              <p className="text-xl md:text-2xl font-light text-gray-500">
                Coste máximo por herramienta
              </p>
            </div>

            <div className="space-y-6">
              <h2 className="text-7xl md:text-8xl lg:text-9xl font-bold text-black dark:text-white leading-none">
                24/7
              </h2>
              <p className="text-xl md:text-2xl font-light text-gray-500">
                Tu negocio nunca cierra
              </p>
            </div>

            <p className="text-lg md:text-xl font-light text-gray-400 pt-16">
              Esto es todo lo que necesitas
            </p>
          </div>
        </AppleSection>

        {/* Cómo Funciona */}
        <AppleSection background="gray" padding="normal">
          <div id="como-funciona" className="text-center max-w-3xl mx-auto space-y-20">
            <div className="space-y-12">
              <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black dark:text-white">
                Identifica la tarea repetitiva
              </p>
              <div className="text-6xl text-gray-400">↓</div>
            </div>

            <div className="space-y-12">
              <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black dark:text-white">
                Configura la automatización
              </p>
              <div className="text-6xl text-gray-400">↓</div>
            </div>

            <div className="space-y-12">
              <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black dark:text-white">
                Deja que trabaje sola
              </p>
            </div>

            <p className="text-lg md:text-xl font-light text-gray-500 pt-16">
              Así de simple
            </p>
          </div>
        </AppleSection>

        {/* Herramientas - DINÁMICO */}
        <ToolListSection
          articles={relatedArticles}
          ctaLabel="Ver todas las automatizaciones"
          ctaLink="/blog"
        />

        {/* Quién lo Usa - Dynamic */}
        <AppleSection background="black" padding="normal">
          <div className="text-center max-w-5xl mx-auto space-y-16">
            <p className="text-2xl md:text-3xl lg:text-4xl font-light text-white dark:text-black leading-relaxed">
              {businessData.types.join('. ')}.
            </p>
          </div>
        </AppleSection>

        {/* Comparativa Split 50/50 */}
        <AppleSection background="white" padding="large">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-32 text-black dark:text-white">
            Todo cambió.
          </h2>
          <AppleSplitComparison
            items={[
              { before: "25 horas/mes", after: "2 horas/mes" },
              { before: "1.200€/mes empleado", after: "10€/mes herramienta" },
              { before: "Errores humanos", after: "Precisión 100%" }
            ]}
          />
        </AppleSection>

        {/* Discovery Pathway & Service Selector */}
        <div className="bg-white dark:bg-black">
          <DiscoveryPathway
            articles={relatedArticles}
            capsulaTheme={capsula.id}
            problemStatement="Estás perdiendo tiempo y dinero en tareas que se pueden automatizar"
          />
          
          <ServiceLevelSelector
            capsulaTheme={capsula.id}
            recommendSupport={recommendSupport}
          />

          {/* FAQ específico */}
          {capsulaFAQs[capsula.id] && (
            <CapsuleFAQ faqs={capsulaFAQs[capsula.id]} />
          )}

          {/* CTA al final - Estilo Jobs */}
          <div className="mt-32 md:mt-40 text-center max-w-4xl mx-auto space-y-8 pb-24">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-foreground tracking-tight leading-tight">
              ¿Quieres que lo automaticemos por ti?
            </h2>
            <p className="text-xl sm:text-2xl md:text-3xl text-muted-foreground font-light leading-relaxed">
              Completa el formulario y cuéntanos qué necesitas
            </p>
            <div className="pt-4">
              <Link 
                to={`/contacto?tipo=servicio&capsula=${capsula.id}`}
                className="group inline-flex items-center gap-4 text-xl md:text-2xl font-light text-foreground hover:opacity-60 transition-opacity"
              >
                <span>Solicitar servicio</span>
                <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
              </Link>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Renderizado específico para CREACIÓN DE VIDEOS
  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {/* Hero Section - Apple Style */}
      <AppleHero
        title={[
          "Crea videos profesionales.",
          "Sin cámara.",
          "Sin equipo.",
          "Solo tu voz."
        ]}
        cta="Ver cómo funciona"
        ctaLink="#como-funciona"
      />

      {/* El Problema */}
      <AppleSection background="black" padding="large">
        <div className="text-center max-w-5xl mx-auto space-y-16">
          <p className="text-4xl md:text-5xl lg:text-6xl font-light text-white dark:text-black leading-tight animate-fade-in">
            Estás gastando 1.500€ y 3 días
            <br />
            en cada video.
          </p>
          <p className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-400 dark:text-gray-600 leading-tight">
            Mientras tu competencia hace 10
            <br />
            en una mañana.
          </p>
        </div>
      </AppleSection>

      {/* La Solución - Tres Números Gigantes */}
      <AppleSection background="white" padding="large">
        <div className="text-center space-y-32 max-w-4xl mx-auto">
          <div className="space-y-6">
            <h2 className="text-7xl md:text-8xl lg:text-9xl font-bold text-black dark:text-white leading-none">
              15 minutos
            </h2>
            <p className="text-xl md:text-2xl font-light text-gray-500">
              De texto a video listo para publicar
            </p>
          </div>

          <div className="space-y-6">
            <h2 className="text-7xl md:text-8xl lg:text-9xl font-bold text-black dark:text-white leading-none">
              0€
            </h2>
            <p className="text-xl md:text-2xl font-light text-gray-500">
              Con las mejores herramientas gratuitas
            </p>
          </div>

          <div className="space-y-6">
            <h2 className="text-7xl md:text-8xl lg:text-9xl font-bold text-black dark:text-white leading-none">
              ∞
            </h2>
            <p className="text-xl md:text-2xl font-light text-gray-500">
              Cambios ilimitados hasta que sea perfecto
            </p>
          </div>

          <p className="text-lg md:text-xl font-light text-gray-400 pt-16">
            Esto es todo lo que necesitas
          </p>
        </div>
      </AppleSection>

      {/* Cómo Funciona */}
      <AppleSection background="gray" padding="normal">
        <div id="como-funciona" className="text-center max-w-3xl mx-auto space-y-20">
          <div className="space-y-12">
            <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black dark:text-white">
              Escribe lo que quieres
            </p>
            <div className="text-6xl text-gray-400">↓</div>
          </div>

          <div className="space-y-12">
            <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black dark:text-white">
              Elige el estilo
            </p>
            <div className="text-6xl text-gray-400">↓</div>
          </div>

          <div className="space-y-12">
            <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black dark:text-white">
              Descarga el video
            </p>
          </div>

          <p className="text-lg md:text-xl font-light text-gray-500 pt-16">
            Así de simple
          </p>
        </div>
      </AppleSection>

      {/* Herramientas - DINÁMICO */}
      <ToolListSection
        articles={relatedArticles}
        ctaLabel="Ver todas las herramientas"
        ctaLink="/blog"
      />

      {/* Quién lo Usa - Dynamic */}
      <AppleSection background="black" padding="normal">
        <div className="text-center max-w-5xl mx-auto space-y-16">
          <p className="text-2xl md:text-3xl lg:text-4xl font-light text-white dark:text-black leading-relaxed">
            {businessData.types.join('. ')}.
          </p>

          {businessData.hasContentCreators && (
            <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white dark:text-black pt-12">
              Si creas contenido,
              <br />
              esto es para ti.
            </p>
          )}
        </div>
      </AppleSection>

      {/* Comparativa Split 50/50 */}
      <AppleSection background="white" padding="large">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-32 text-black dark:text-white">
          Todo cambió.
        </h2>
        <AppleSplitComparison
          items={[
            { before: "1-3 días", after: "15 minutos" },
            { before: "500-1.500€", after: "0€" },
            { before: "Equipo completo", after: "Tu ordenador" }
          ]}
        />
      </AppleSection>

      {/* Discovery Pathway & Service Selector */}
      <div className="bg-white dark:bg-black">
        <DiscoveryPathway
          articles={relatedArticles}
          capsulaTheme={capsula.id}
          problemStatement="No tienes un gran presupuesto para crear videos de calidad"
        />
        
        <ServiceLevelSelector
          capsulaTheme={capsula.id}
          recommendSupport={recommendSupport}
        />

        {/* FAQ específico */}
        {capsulaFAQs[capsula.id] && (
          <CapsuleFAQ faqs={capsulaFAQs[capsula.id]} />
        )}

        {/* CTA al final - Estilo Jobs */}
        <div className="mt-32 md:mt-40 text-center max-w-4xl mx-auto space-y-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-foreground tracking-tight leading-tight">
            ¿Quieres que lo hagamos por ti?
          </h2>
          <p className="text-xl sm:text-2xl md:text-3xl text-muted-foreground font-light leading-relaxed">
            Completa el formulario y cuéntanos qué necesitas
          </p>
          <div className="pt-4">
            <Link 
              to={`/contacto?tipo=servicio&capsula=${capsula.id}`}
              className="group inline-flex items-center gap-4 text-xl md:text-2xl font-light text-foreground hover:opacity-60 transition-opacity"
            >
              <span>Solicitar servicio</span>
              <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CapsulaDetail;
